import { useSyncExternalStore } from 'react'

export type AdjustmentType = 'fee' | 'recoupment'

export interface FeeRecoupment {
  id: string
  type: AdjustmentType
  userId: string
  // Optional: when set, the adjustment is tied to a specific funded loan.
  loanId: string | null
  loanLabel: string | null // Cached borrower label for display
  label: string
  amount: number // Positive dollar amount owed by the loan officer
  date: string // ISO date string, e.g. 2026-06-12
  note: string
}

// Seed data so the feature is populated on first load. Targets a couple of the
// generated mock users by their deterministic ids (user-1, user-3, ...).
let adjustments: FeeRecoupment[] = [
  {
    id: 'adj-seed-1',
    type: 'fee',
    userId: 'user-1',
    loanId: null,
    loanLabel: null,
    label: 'Compliance training fee',
    amount: 250,
    date: '2026-06-05',
    note: 'Q2 mandatory compliance course.',
  },
  {
    id: 'adj-seed-2',
    type: 'recoupment',
    userId: 'user-1',
    loanId: null,
    loanLabel: null,
    label: 'Early payoff clawback',
    amount: 1200,
    date: '2026-06-18',
    note: 'Borrower refinanced within 90 days.',
  },
  {
    id: 'adj-seed-3',
    type: 'fee',
    userId: 'user-3',
    loanId: null,
    loanLabel: null,
    label: 'Marketing co-op',
    amount: 400,
    date: '2026-06-10',
    note: 'Shared Zillow ad spend.',
  },
]

const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot() {
  return adjustments
}

export function addAdjustment(input: Omit<FeeRecoupment, 'id'>) {
  const id = `adj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  adjustments = [{ ...input, id }, ...adjustments]
  emit()
}

export function removeAdjustment(id: string) {
  adjustments = adjustments.filter((a) => a.id !== id)
  emit()
}

/** Subscribe to the full list of adjustments (admin view). */
export function useAdjustments(): FeeRecoupment[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Subscribe to the adjustments that belong to a single user (LO view). */
export function useUserAdjustments(userId: string | null): FeeRecoupment[] {
  const all = useAdjustments()
  if (!userId) return []
  return all.filter((a) => a.userId === userId)
}
